import { faClose, faCopy, faKey, faLink, faRobot, faServer, faShieldHalved } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import type { IPlatformWhatsAppSettings } from "../../../Objects/PlatformWhatsApp/IPlatformWhatsAppSettings"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"

export const RESPOSTA_AI_DEFAULT_BASE_URL = "https://respostaai.com.br"

type Props = {
  settings: IPlatformWhatsAppSettings
  onChange: (value: IPlatformWhatsAppSettings) => void
  onSave: () => void
  onClose: () => void
}

export const PlatformWhatsAppRespostaAiSettingsPopup = ({ settings, onChange, onSave, onClose }: Props) => {
  const { t } = useTranslation("platformWhatsApp")

  const [copied, setCopied] = useState(false)

  const patch = (value: Partial<IPlatformWhatsAppSettings>) => onChange({ ...settings, ...value })

  const webhookUrl = `${API_ROUTES.URL_API}/api/admin/whatsapp-saas/webhook`

  const copyWebhookUrl = async () => {
    await navigator.clipboard.writeText(webhookUrl)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={t("respostaAi.title")}
              icon={faRobot}
              iconColor={THEME.colors.iconBGColor}
            >
              <ButtonIcon
                sizeNumber={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGCloseColor}
                iconColor={THEME.colors.buttonFGCloseColor}
                icon={faClose}
                onClick={onClose}
                size="small"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppRespostaAiSettingsPopup.button.onClose"
              />
            </TitlePageResponsiveIconSmall>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <div className="max-h-[75vh] space-y-3 overflow-x-hidden overflow-y-auto p-4">
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
                <p className="text-sm text-slate-500">{t("respostaAi.description")}</p>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.1s">
                <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-3">
                  <p className="mb-2 text-xs font-semibold text-slate-600">{t("respostaAi.webhookUrlLabel")}</p>

                  <div className="flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded-lg bg-white px-3 py-2 text-xs text-slate-700">
                      {webhookUrl}
                    </code>

                    <button
                      type="button"
                      className="shrink-0 rounded-lg bg-emerald-600 px-3 py-2 text-white transition hover:bg-emerald-700"
                      onClick={copyWebhookUrl}
                      data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppRespostaAiSettingsPopup.button.copyWebhookUrl"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>

                  {copied && <p className="mt-1 text-xs font-semibold text-emerald-600">{t("respostaAi.copied")}</p>}

                  <p className="mt-2 text-xs text-slate-500">{t("respostaAi.webhookUrlHint")}</p>
                </div>
              </Animate>

              <InputField
                type="text"
                icon={faServer}
                value={settings.whatsAppSaaSInstanceId ?? ""}
                placeholder={t("respostaAi.instanceId")}
                onChange={(event) => patch({ whatsAppSaaSInstanceId: event.target.value || null })}
                animationDuration="1.15s"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppRespostaAiSettingsPopup.input.instanceId"
              />
              <p className="text-xs text-slate-400">{t("respostaAi.instanceIdExample")}</p>

              <InputField
                type="link"
                icon={faLink}
                value={settings.whatsAppSaaSBaseUrl}
                placeholder={t("respostaAi.baseUrl")}
                onChange={(event) => patch({ whatsAppSaaSBaseUrl: event.target.value })}
                animationDuration="1.2s"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppRespostaAiSettingsPopup.input.baseUrl"
              />

              <InputField
                type="password"
                icon={faKey}
                value={settings.whatsAppSaaSApiKey}
                placeholder={settings.hasWhatsAppSaaSApiKey ? t("respostaAi.apiKeyStored") : t("respostaAi.apiKey")}
                onChange={(event) => patch({ whatsAppSaaSApiKey: event.target.value })}
                animationDuration="1.25s"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppRespostaAiSettingsPopup.input.apiKey"
              />
              <p className="text-xs text-slate-400">{t("respostaAi.apiKeyExample")}</p>

              <InputField
                type="password"
                icon={faShieldHalved}
                value={settings.whatsAppSaaSWebhookSecret}
                placeholder={
                  settings.hasWhatsAppSaaSWebhookSecret
                    ? t("respostaAi.webhookSecretStored")
                    : t("respostaAi.webhookSecret")
                }
                onChange={(event) => patch({ whatsAppSaaSWebhookSecret: event.target.value })}
                animationDuration="1.3s"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppRespostaAiSettingsPopup.input.webhookSecret"
              />
              <p className="text-xs text-slate-400">{t("respostaAi.webhookSecretExample")}</p>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.35s">
                <p className="rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  {t("respostaAi.botProfileNote")}
                </p>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
                <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    className="rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white shadow-sm transition-all duration-200 hover:bg-emerald-700 hover:shadow-md active:scale-95"
                    onClick={onSave}
                    data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppRespostaAiSettingsPopup.button.onSave"
                  >
                    {t("actions.save")}
                  </button>
                </div>
              </Animate>
            </div>
            </Animate>
          </div>

        </Animate>
      </div>
    </ModalPortal>
  )
}
